import { useState, useEffect, useCallback, useRef } from "react";

const NOTIF_SETTINGS_KEY = "crm_notification_settings";
const NOTIF_LAST_CHECK_KEY = "crm_notification_last_check";

const DEFAULT_SETTINGS = {
  enabled: false,
  clockOutReminder: true,
  clockOutAfterHours: 8,
  einsatzplanChanges: true,
  pollIntervalMinutes: 5,
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(NOTIF_SETTINGS_KEY);
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_SETTINGS };
}

function saveSettings(settings) {
  localStorage.setItem(NOTIF_SETTINGS_KEY, JSON.stringify(settings));
}

function canNotify() {
  return "Notification" in window && Notification.permission === "granted";
}

function sendNotification(title, body, tag) {
  if (!canNotify()) return;
  try {
    new Notification(title, {
      body,
      tag, // prevents duplicate notifications with same tag
      icon: "/favicon.ico",
      badge: "/favicon.ico",
    });
  } catch {}
}

export function useNotifications(authFetch, apiBase) {
  const [settings, setSettings] = useState(loadSettings);
  const [permissionState, setPermissionState] = useState(
    "Notification" in window ? Notification.permission : "denied"
  );
  const clockInTimeRef = useRef(null);
  const reminderSentRef = useRef(false);
  const pollRef = useRef(null);

  // Request permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return "denied";
    const perm = await Notification.requestPermission();
    setPermissionState(perm);
    if (perm === "granted") {
      const newSettings = { ...settings, enabled: true };
      setSettings(newSettings);
      saveSettings(newSettings);
    }
    return perm;
  }, [settings]);

  // Update settings
  const updateSettings = useCallback((updates) => {
    setSettings((prev) => {
      const next = { ...prev, ...updates };
      saveSettings(next);
      return next;
    });
  }, []);

  // Clock-out reminder logic
  useEffect(() => {
    if (!settings.enabled || !settings.clockOutReminder) return;

    const checkClockStatus = async () => {
      try {
        const res = await authFetch(`${apiBase}/clock/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (data && data.clock_in && !data.clock_out) {
          const clockIn = new Date(data.clock_in);
          clockInTimeRef.current = clockIn;
          const hoursWorked = (Date.now() - clockIn.getTime()) / (1000 * 60 * 60);
          if (hoursWorked >= settings.clockOutAfterHours && !reminderSentRef.current) {
            reminderSentRef.current = true;
            sendNotification(
              "Ausstempel-Erinnerung",
              `Sie arbeiten seit ${Math.floor(hoursWorked)} Stunden. Vergessen Sie nicht auszustempeln!`,
              "clock-out-reminder"
            );
          }
        } else {
          reminderSentRef.current = false;
          clockInTimeRef.current = null;
        }
      } catch {}
    };

    checkClockStatus();
    const interval = setInterval(checkClockStatus, 60 * 1000); // Check every minute
    return () => clearInterval(interval);
  }, [settings.enabled, settings.clockOutReminder, settings.clockOutAfterHours, authFetch, apiBase]);

  // Einsatzplan change polling
  useEffect(() => {
    if (!settings.enabled || !settings.einsatzplanChanges) return;

    const checkEinsatzplan = async () => {
      try {
        const lastCheck = localStorage.getItem(NOTIF_LAST_CHECK_KEY) || new Date().toISOString();
        const res = await authFetch(`${apiBase}/notifications/check?since=${encodeURIComponent(lastCheck)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.einsatzplan_changes > 0) {
            sendNotification(
              "Einsatzplan aktualisiert",
              `${data.einsatzplan_changes} neue Änderung(en) in Ihrem Einsatzplan.`,
              "einsatzplan-change"
            );
          }
        }
        localStorage.setItem(NOTIF_LAST_CHECK_KEY, new Date().toISOString());
      } catch {}
    };

    const intervalMs = settings.pollIntervalMinutes * 60 * 1000;
    checkEinsatzplan();
    pollRef.current = setInterval(checkEinsatzplan, intervalMs);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [settings.enabled, settings.einsatzplanChanges, settings.pollIntervalMinutes, authFetch, apiBase]);

  return {
    settings,
    updateSettings,
    permissionState,
    requestPermission,
    canNotify: canNotify(),
  };
}
